<template>
  <v-dialog v-model="value" max-width="850px" persistent>
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5 sp">{{ $t('colleges.create') }}</span>
      </v-card-title>

      <template v-if="!created">
        <v-tabs v-model="tab" background-color="transparent" grow>
          <v-tab>{{ $t('colleges.tab_school') }}</v-tab>
          <v-tab>{{ $t('colleges.tab_contract') }}</v-tab>
        </v-tabs>

        <v-card-text>
          <v-tabs-items v-model="tab">
            <!-- ── Tab 1: Colegio ── -->
            <v-tab-item>
              <v-container>
                <v-form ref="form" v-model="valid" lazy-validation>
                  <v-row>
                    <v-col cols="12" md="8">
                      <v-text-field
                        v-model="form.name"
                        :label="$t('colleges.school_name_label')"
                        :rules="[rules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model.number="form.operationYear"
                        :label="$t('colleges.operation_year_label')"
                        type="number"
                        :rules="[rules.required, rules.year]"
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="form.baseCountryId"
                        :items="countries"
                        item-text="name"
                        item-value="id"
                        :label="$t('colleges.country_label')"
                        :rules="[rules.required]"
                        @change="onCountryChange"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.calendarTypeId"
                        :items="calendarTypes"
                        item-text="name"
                        item-value="id"
                        :label="$t('colleges.calendar_type')"
                        :rules="[rules.required]"
                        :loading="loadingCalendarTypes"
                      />
                    </v-col>
                  </v-row>

                  <v-row v-if="showState || showTimezone">
                    <v-col v-if="showState" cols="12" md="4">
                      <v-autocomplete
                        v-model="form.state"
                        :items="usStates"
                        :label="$t('colleges.state_label')"
                        clearable
                        @change="onStateChange"
                      />
                    </v-col>
                    <v-col v-if="showTimezone" cols="12" md="4">
                      <v-select
                        v-model="form.timeZone"
                        :items="timezoneOptions"
                        item-text="label"
                        item-value="value"
                        :label="$t('colleges.time_zone_label')"
                        clearable
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.paymentByTypeId"
                        :items="paymentTypes"
                        item-text="text"
                        item-value="value"
                        :label="$t('colleges.payment_identifier')"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="form.schoolGroupId"
                        :items="schoolGroups"
                        item-text="name"
                        item-value="id"
                        :label="$t('colleges.school_group_label')"
                        :disabled="!form.baseCountryId"
                        :loading="loadingGroups"
                        clearable
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.rbd"
                        :label="$t('colleges.rbd_label')"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.contact"
                        :label="$t('colleges.contact_label')"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.phone"
                        :label="$t('colleges.phone_label')"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-tab-item>

            <!-- ── Tab 2: Contrato (opcional) ── -->
            <v-tab-item>
              <v-container>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contract.name"
                      :label="$t('colleges.arrangement_name') + ' (opcional)'"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="contract.arrangementType"
                      :items="arrangementTypes"
                      item-text="text"
                      item-value="value"
                      :label="$t('colleges.enrollment_type')"
                      :error-messages="contractErrors.arrangementType"
                      clearable
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="contract.totalLicenses"
                      type="number"
                      min="1"
                      :label="$t('colleges.total_licenses')"
                      :error-messages="contractErrors.totalLicenses"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contract.emailCustomerCs"
                      :label="$t('colleges.email_cs_label') + ' (opcional)'"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="menuStartDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="contract.startDate"
                          :label="$t('colleges.start_date')"
                          prepend-icon="mdi-calendar"
                          readonly
                          :error-messages="contractErrors.startDate"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker v-model="contract.startDate" @input="menuStartDate = false" />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="menuEndDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="contract.endDate"
                          :label="$t('colleges.end_date')"
                          prepend-icon="mdi-calendar"
                          readonly
                          :error-messages="contractErrors.endDate"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker v-model="contract.endDate" @input="menuEndDate = false" />
                    </v-menu>
                  </v-col>
                </v-row>

                <v-row v-if="showContractErrors && contractDateError">
                  <v-col>
                    <span class="red--text caption">
                      {{ $t('colleges.validation_start_before_end') }}
                    </span>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <span class="grey--text caption">
                      Los campos del contrato son opcionales. Si deseas registrar un acuerdo,
                      completa al menos Tipo de Acuerdo, Total de licencias y las fechas.
                    </span>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </template>

      <v-card-text v-if="created" class="text-center">
        <v-container>
          <v-icon size="48" color="green">mdi-check-circle</v-icon>
          <h3 class="mt-2">{{ $t('colleges.created_title') }}</h3>
          <p class="mt-2">{{ createdInfo }}</p>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="!created">
          <v-btn color="grey" text @click="close">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="deep-purple" text :disabled="!valid" @click="save">
            {{ $t('common.save') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="deep-purple" text @click="resetForAnother">
            {{ $t('colleges.create_another') }}
          </v-btn>
          <v-btn color="grey" text @click="close">{{ $t('common.close') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  US_STATES, STATE_TO_TZ, US_TIMEZONES, MX_TIMEZONES,
} from '@/constants/locations';
import countryService from '../../../services/country.service';
import conglomerateService from '../../../services/conglomerate.service';
import schoolCreateService from '../../../services/school-create.service';
import schoolArrangementService from '../../../services/schoolArrangement.service';
import calendarTypeService from '../../../services/calendarType.service';
import snackbarMixin from '../../../mixins/snackbar';
import { COUNTRIES_CUSTOMER_ID } from '../../../constants/data';
import { formatPaymentByTypeIdentifierOptions } from '../../../constants/data/FormatInputMassiveLoad.json';

const emptyForm = () => ({
  name: '',
  baseCountryId: null,
  operationYear: new Date().getFullYear(),
  calendarTypeId: null,
  paymentByTypeId: null,
  schoolGroupId: null,
  rbd: '',
  contact: '',
  phone: '',
  state: null,
  timeZone: null,
});

const emptyContract = () => ({
  name: '',
  arrangementType: null,
  totalLicenses: null,
  emailCustomerCs: '',
  startDate: '',
  endDate: '',
});

export default {
  name: 'CreateSchoolModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
  },
  data: () => ({
    tab: 0,
    loading: false,
    loadingGroups: false,
    loadingCalendarTypes: false,
    valid: false,
    countries: [],
    schoolGroups: [],
    calendarTypes: [],
    paymentTypes: formatPaymentByTypeIdentifierOptions,
    form: emptyForm(),
    contract: emptyContract(),
    menuStartDate: false,
    menuEndDate: false,
    showContractErrors: false,
    created: false,
    createdId: null,
    rules: {
      required: (v) => (v !== null && v !== undefined && v !== '') || 'Requerido.',
      year: (v) => (Number.isInteger(Number(v)) && Number(v) >= 1900) || 'Año inválido.',
    },
  }),
  computed: {
    selectedCountryIso() {
      return this.countries.find((c) => c.id === this.form.baseCountryId)?.iso_code ?? null;
    },
    showState() {
      return this.selectedCountryIso === 'USA';
    },
    showTimezone() {
      return this.selectedCountryIso === 'USA' || this.selectedCountryIso === 'MEX';
    },
    timezoneOptions() {
      return this.selectedCountryIso === 'MEX' ? MX_TIMEZONES : US_TIMEZONES;
    },
    usStates() {
      return US_STATES;
    },
    arrangementTypes() {
      return [
        { text: this.$t('conglomerates.type_demo'), value: 'Demo' },
        { text: this.$t('conglomerates.type_trial'), value: 'Trial' },
        { text: this.$t('conglomerates.type_client'), value: 'Client' },
      ];
    },
    hasContractData() {
      const c = this.contract;
      return !!(
        c.arrangementType || c.totalLicenses || c.startDate
        || c.endDate || c.emailCustomerCs || c.name
      );
    },
    contractDateError() {
      if (!this.contract.startDate || !this.contract.endDate) return false;
      return this.contract.endDate <= this.contract.startDate;
    },
    contractErrors() {
      if (!this.showContractErrors || !this.hasContractData) {
        return {
          arrangementType: [], totalLicenses: [], startDate: [], endDate: [],
        };
      }
      return {
        arrangementType: this.contract.arrangementType ? [] : ['Requerido.'],
        totalLicenses: (!this.contract.totalLicenses || this.contract.totalLicenses <= 0)
          ? ['Requerido.']
          : [],
        startDate: this.contract.startDate ? [] : ['Requerido.'],
        endDate: this.endDateErrors(),
      };
    },
    contractIsValid() {
      if (!this.hasContractData) return true;
      const errors = this.contractErrors;
      return (
        errors.arrangementType.length === 0
        && errors.totalLicenses.length === 0
        && errors.startDate.length === 0
        && errors.endDate.length === 0
        && !this.contractDateError
      );
    },
    createdInfo() {
      return this.$t('colleges.created_info', {
        id: this.createdId,
        name: this.form.name,
      });
    },
  },
  async mounted() {
    await Promise.all([this.loadCountries(), this.loadCalendarTypes()]);
  },
  methods: {
    async loadCountries() {
      try {
        const { data } = await countryService.findAll();
        this.countries = (data || []).filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
      } catch (e) {
        this.countries = [];
      }
    },
    async loadCalendarTypes() {
      this.loadingCalendarTypes = true;
      try {
        const { data } = await calendarTypeService.findAll(false);
        this.calendarTypes = data || [];
      } catch (e) {
        // Calendar type is required to create a school, so a failed load must
        // not be silent: surface it and keep a diagnostic trace.
        this.calendarTypes = [];
        this.snackbarError(this.$t('colleges.calendar_type_load_error'));
        console.error('[CreateSchoolModal] loadCalendarTypes failed', e);
      } finally {
        this.loadingCalendarTypes = false;
      }
    },
    async loadSchoolGroups(countryId) {
      this.loadingGroups = true;
      try {
        const result = await conglomerateService.list({ country_id: countryId, limit: 200 });
        this.schoolGroups = result.content ? result.content.items : [];
      } catch (e) {
        this.schoolGroups = [];
      } finally {
        this.loadingGroups = false;
      }
    },
    endDateErrors() {
      if (!this.contract.endDate) return ['Requerido.'];
      if (this.contractDateError) return ['La fecha fin debe ser posterior a la de inicio.'];
      return [];
    },
    onCountryChange() {
      this.form.schoolGroupId = null;
      this.schoolGroups = [];
      if (this.form.baseCountryId) this.loadSchoolGroups(this.form.baseCountryId);
      // Clear state/timeZone on EVERY country change. showTimezone is true for
      // both USA and MEX, so a USA<->MEX switch must not retain a TZ from the
      // other country's (disjoint) set. This is user-driven, so there is no
      // preloaded value to preserve; the user (or state->TZ suggestion) repopulates.
      this.form.state = null;
      this.form.timeZone = null;
    },
    onStateChange(state) {
      this.form.timeZone = state ? (STATE_TO_TZ[state] ?? null) : null;
    },
    async save() {
      this.showContractErrors = true;
      if (!this.$refs.form.validate()) return;
      if (!this.contractIsValid) {
        this.tab = 1;
        this.snackbarError('Revisa los datos del contrato.');
        return;
      }

      this.loading = true;
      try {
        const schoolResult = await schoolCreateService.create({
          name: this.form.name,
          baseCountryId: this.form.baseCountryId,
          operationYear: Number(this.form.operationYear),
          calendarTypeId: this.form.calendarTypeId,
          paymentByTypeId: this.form.paymentByTypeId || undefined,
          schoolGroupId: this.form.schoolGroupId || undefined,
          rbd: this.form.rbd || undefined,
          contact: this.form.contact || undefined,
          phone: this.form.phone || undefined,
          state: this.form.state || undefined,
          timeZone: this.form.timeZone || undefined,
        });

        this.createdId = schoolResult.content.id;

        if (this.hasContractData) {
          try {
            await schoolArrangementService.create({
              name: this.contract.name || undefined,
              arrangementType: this.contract.arrangementType,
              totalLicenses: this.contract.totalLicenses,
              emailCustomerCs: this.contract.emailCustomerCs || undefined,
              startDate: this.contract.startDate,
              endDate: this.contract.endDate,
              schoolId: this.createdId,
            });
          } catch (arrangementError) {
            this.snackbarError(this.$t('colleges.create_arrangement_error'));
          }
        }

        this.created = true;
        this.$emit('onSave');
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 409) {
          this.snackbarError(this.$t('colleges.name_exists'));
        } else {
          const msg = error.response && error.response.data && error.response.data.message;
          this.snackbarError(msg || this.$t('colleges.create_error'));
        }
      } finally {
        this.loading = false;
      }
    },
    resetForAnother() {
      this.tab = 0;
      this.form = emptyForm();
      this.contract = emptyContract();
      this.created = false;
      this.createdId = null;
      this.showContractErrors = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.tab = 0;
      this.form = emptyForm();
      this.contract = emptyContract();
      this.created = false;
      this.createdId = null;
      this.showContractErrors = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.$emit('onCancel');
    },
  },
};
</script>

<style scoped>
span.sp {
  font-family: "Space Grotesk" !important;
}
</style>
